import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../../components/client/ClientLayout';
import { getCurrentUser } from '../../lib/auth';

const ClientPortal = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  const fetchUserInfo = useCallback(async () => {
    try {
      // Get user from Cloudflare Access
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await fetch('/client/api/user/me', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUserInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      // Get user from Cloudflare Access
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        throw new Error('User is not authenticated');
      }

      const response = await fetch('/client/api/client/projects', {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAuthAndLoadData = useCallback(async () => {
    try {
      // Check if user is authenticated via Cloudflare Access
      const user = await getCurrentUser();
      
      if (!user || !user.authenticated) {
        // No authentication found - this shouldn't happen since /client* is protected
        // Cloudflare Access should have intercepted before reaching here
        window.location.href = '/client';
        return;
      }
      
      // User is authenticated, load data
      await fetchUserInfo();
      await fetchProjects();
    } catch (error) {
      console.error('Authentication check failed:', error);
      window.location.href = '/client';
    }
  }, [fetchUserInfo, fetchProjects]);

  useEffect(() => {
    checkAuthAndLoadData();
  }, [checkAuthAndLoadData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleProjectClick = (projectId) => {
    navigate(`/client/projects/${projectId}`);
  };

  const handleLogout = async () => {
    try {
      // First, log the logout action
      const response = await fetch('/client/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirect to Cloudflare Access logout
        window.location.href = data.redirect_url || '/cdn-cgi/access/logout';
      } else {
        // If logging fails, still redirect to logout
        window.location.href = '/cdn-cgi/access/logout';
      }
    } catch (error) {
      console.error('Failed to log logout:', error);
      // If logging fails, still redirect to logout
      window.location.href = '/cdn-cgi/access/logout';
    }
  };

  if (loading) {
    return (
      <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue-600 mx-auto"></div>
            <p className="mt-4 text-brand-dark">Loading your projects...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-sm">
              <p className="font-semibold">Error Loading Projects</p>
              <p className="mt-1">{error}</p>
            </div>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 bg-brand-blue-600 text-white px-6 py-2 rounded-md hover:bg-brand-blue-700 transition-colors shadow-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout userInfo={userInfo} onLogout={handleLogout}>
      {/* Welcome Section */}
      <div className="bg-brand-black shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Welcome to Lone Star Aerial Solutions{userInfo?.client_name ? `, ${userInfo.client_name}` : ''}
            </h1>
            <p className="text-lg text-ink">
              Your deliverables, secured and ready.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Your Projects</h2>
          <p className="text-white">
            Click on any project to view and download your deliverables
          </p>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto h-16 w-16 text-gray-400 flex items-center justify-center text-4xl mb-4">
              📁
            </div>
            <h3 className="text-xl font-medium text-brand-dark mb-2">No projects yet</h3>
            <p className="text-gray-900 max-w-md mx-auto">
              You don't have any projects yet. If you expected to see something here, contact{' '}
              <a 
                href="mailto:info@lonestaraerialsolutions.com" 
                className="text-brand-blue-600 hover:text-brand-blue-700 underline"
              >
                info@lonestaraerialsolutions.com
              </a>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                className="bg-white rounded-xl shadow-card hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-100 hover:border-brand-blue-600/30 group"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="h-10 w-10 text-brand-blue-600 mr-4 flex items-center justify-center text-2xl bg-brand-blue-600/10 rounded-lg group-hover:bg-brand-blue-600/20 transition-colors">
                      📁
                    </div>
                    <h3 className="text-lg font-semibold text-brand-dark truncate group-hover:text-brand-blue-600 transition-colors">
                      {project.title}
                    </h3>
                  </div>
                  
                  {project.notes && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {project.notes}
                    </p>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span className="mr-2">📅</span>
                    <span>Created {formatDate(project.created_at)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-blue-600/10 text-brand-blue-600 border border-brand-blue-600/20">
                      {project.client_name}
                    </span>
                    <span className="text-brand-blue-600 text-sm font-medium group-hover:text-brand-blue-700 flex items-center">
                      View Details 
                      <span className="ml-1 transform group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientPortal;
